import React, { Dispatch, SetStateAction } from 'react'
import { Typography, styled } from '@mui/material'
import { ColumnDef, Row } from '@tanstack/react-table'
import { useParams } from 'react-router-dom'
import {
  WebsiteRouteBuilder,
  PositionRouteBuilder,
  AdGroupRouteBuilder,
  AdCopyRouteBuilder,
  AllRoute,
  AdTemplateRouteBuilder,
  CampaignRouteBuilder,
  PublisherRouteBuilder,
  AdvertiserRouteBuilder,
} from '../../../pages/routeParams'
import { StatsConf } from '../../../components/stats/statsConf'
import { formatSessionTimeSeconds } from '../../formatSessionTimeSeconds'
import {
  ADGROUP_DATA_TYPE,
  ADVERTISER_DATA_TYPE,
  AGENCY_DATA_TYPE,
  CAMPAIGN_DATA_TYPE,
  COMPANY_BASE_STAT_DATA_TYPE,
  POSITION_DATA_TYPE,
  PUBLISHER_DATA_TYPE,
  RESELLER_ADVERTISER_DATA_TYPE,
  RESELLER_PUBLISHER_DATA_TYPE,
  WEBSITE_DATA_TYPE,
} from '../../../interfaces/stats'
import { InputOptions } from '../../../components/form/Options'
import { isNil } from 'lodash-es'
import { mobileLayoutStyles } from '../useIsMobileLayout'
export { useAdvertiserStatsColumns } from './useAdvertiserStatsColumns'
export { usePublisherStatsColumns } from './usePublisherStatsColumns'

const ANALYTICS_COLUMNS = new Set([
  'pageViews',
  'bounceRate',
  'totalUsers',
  'newUsers',
  'sessions',
  'engagedSessions',
  'sessionTimeSeconds',
  'cpv',
  'engagementRate',
])

const STATS_TABLE_COLUMNS = new Set(['dailyBudget', 'channelType'])

const PUBLISHER_COLUMNS = new Set(['displays', 'commissions'])

const COMMONS_METRIC_COLUMNS = new Set([
  'clicks',
  'costs',
  'cpm',
  'ctr',
  'impressions',
  'leads',
  'orders',
  'revenue',
  'status',
])

export const getPerformanceStatsColumnTranslationKey = (columnName: string): string => {
  if (COMMONS_METRIC_COLUMNS.has(columnName)) return `commons.${columnName}`
  if (ANALYTICS_COLUMNS.has(columnName)) return `adgroup.analytics.${columnName}`
  if (STATS_TABLE_COLUMNS.has(columnName)) return `stats.table.${columnName}`
  if (PUBLISHER_COLUMNS.has(columnName)) return `stats.customDisplay.publisher.${columnName}`

  return `stats.customDisplay.advertiser.${columnName}`
}

export const FirstColumn = styled('div')(({ theme }) => ({
  minWidth: '320px',
  ...mobileLayoutStyles(theme, {
    minWidth: 0,
  }),
}))

export const OtherColumn = styled('div')(({ theme }) => ({
  minWidth: '56px',
  ...mobileLayoutStyles(theme, {
    minWidth: 0,
  }),
}))

export interface GroupColumnsType {
  group: number
  columnsName: Array<string>
}

interface MakeLinkInterface<T> {
  type: string
  id?: string
  row: Row<T>
}

export type HookOptions<T extends string> = {
  dateFormat: string
  timezone: string
  selectedDataType: T
  selectedDataTypeChange: Dispatch<SetStateAction<T>>
  allDataTypes: InputOptions[]
}

export const makeLink = <T,>(options: MakeLinkInterface<T>): string => {
  const { slug, adgroupId, positionId } = useParams<AllRoute>()
  const { type, id, row } = options
  if (!type || !row) return ''

  const publisherDataTypes = [
    RESELLER_PUBLISHER_DATA_TYPE.PUBLISHERS,
    RESELLER_ADVERTISER_DATA_TYPE.PUBLISHERS,
    AGENCY_DATA_TYPE.PUBLISHERS,
  ]
  if (publisherDataTypes.includes(type)) {
    const publisherSlug = id ?? (row.original as { slug?: string })?.slug
    return publisherSlug ? PublisherRouteBuilder(publisherSlug) : ''
  }

  const advertiserDataTypes = [RESELLER_ADVERTISER_DATA_TYPE.ADVERTISERS, AGENCY_DATA_TYPE.ADVERTISERS]
  if (advertiserDataTypes.includes(type)) {
    const advertiserSlug = (row.original as { slug?: string })?.slug ?? id
    return advertiserSlug ? AdvertiserRouteBuilder(advertiserSlug) : ''
  }

  const campaignDataTypes = [
    POSITION_DATA_TYPE.CAMPAIGNS,
    ADVERTISER_DATA_TYPE.CAMPAIGNS,
    RESELLER_ADVERTISER_DATA_TYPE.CAMPAIGNS,
    PUBLISHER_DATA_TYPE.CAMPAIGNS,
  ]
  if (campaignDataTypes.includes(type) && !isNil(id)) {
    const campaignSlug = (row.original as { slug?: string })?.slug ?? slug
    if (campaignSlug) return CampaignRouteBuilder(campaignSlug, id)
  }

  if (!id) return ''
  if (type === COMPANY_BASE_STAT_DATA_TYPE.WEBSITES_AND_POSITIONS && row.depth === 0)
    return WebsiteRouteBuilder(slug, id)
  else if (type === COMPANY_BASE_STAT_DATA_TYPE.WEBSITES_AND_POSITIONS && row.depth === 1)
    return PositionRouteBuilder(slug, id)
  else if (type === WEBSITE_DATA_TYPE.POSITIONS) return PositionRouteBuilder(slug, id)
  if (type === POSITION_DATA_TYPE.ADTEMPLATES && positionId) return AdTemplateRouteBuilder(slug, positionId, id)
  if (type === POSITION_DATA_TYPE.ZONES && row.depth === 1 && positionId)
    return AdTemplateRouteBuilder(slug, positionId, id)
  if (type === CAMPAIGN_DATA_TYPE.ADGROUPS) return AdGroupRouteBuilder(slug, id)
  if (type === ADGROUP_DATA_TYPE.ADCOPIES && adgroupId) return AdCopyRouteBuilder(slug, adgroupId, id)
  else return ''
}

export const formatNumber = (value: string | number, style?: string): string => {
  if (typeof value !== 'number') return value
  if (style === 'duration') return formatSessionTimeSeconds(value)

  const valueToDisplay = style === 'percent' ? value / 100 : value

  const maximumFractionDigits = 2
  const minimumFractionDigits = style === 'percent' ? maximumFractionDigits : 0

  return Intl.NumberFormat('fr-FR', {
    style: style,
    useGrouping: true,
    minimumFractionDigits,
    maximumFractionDigits,
    currency: 'EUR',
  }).format(valueToDisplay)
}

export const createNumericColumn = <T,>(
  columnName: string,
  header: string,
  group: number | undefined,
  total: T
): ColumnDef<T> => {
  return {
    accessorKey: columnName,
    header: () => <OtherColumn>{header}</OtherColumn>,
    cell: (info) => (
      <>{formatNumber((info.getValue() ?? 0) as number, StatsConf[columnName as keyof typeof StatsConf]?.style)}</>
    ),
    footer: () => (
      <>
        {formatNumber(
          (total[columnName as keyof typeof total] ?? 0) as number,
          StatsConf[columnName as keyof typeof StatsConf]?.style
        )}
      </>
    ),
    meta: {
      ...(group ? { group } : {}),
      headerSx: {
        textAlign: 'right',
        '& .MuiTableSortLabel-root': {
          justifyContent: 'flex-end',
          width: '100%',
        },
      },
      cellSx: {
        fontVariantNumeric: 'tabular-nums',
        textAlign: 'right',
        whiteSpace: { md: 'nowrap', xs: 'normal' },
      },
    },
  }
}

export const formatNameWithAdditionalInfo = (value: string, additionalInfo?: string): JSX.Element => {
  return (
    <Typography
      variant="body2"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        lineHeight: 1.35,
      }}
    >
      <span>{value}</span>
      {additionalInfo && (
        <Typography component="span" variant="caption" color="textSecondary">
          {additionalInfo}
        </Typography>
      )}
    </Typography>
  )
}
